import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Gift, Copy, Check, Mail, MessageCircle, Users, Trophy, Coins, Loader2, UserPlus, Link2,
} from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="kite-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4" style={{ color: accent }} />
      <span className="label-eyebrow text-muted-foreground">{label}</span>
    </div>
    <div className="font-data text-3xl font-semibold" style={{ color: accent }}>{value}</div>
  </div>
);

export default function Referrals() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/referrals/me?origin=${encodeURIComponent(window.location.origin)}`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e, t('referrals.loadError')));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const link = data?.link || '';
  const referrer = data?.rewards?.referrer ?? 2;
  const referee = data?.rewards?.referee ?? 2;
  const shareText = t('referrals.shareText', { referee });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(t('referrals.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('referrals.copyError'));
    }
  };

  const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${link}`)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(t('referrals.mailSubject'))}&body=${encodeURIComponent(`${shareText}\n\n${link}`)}`;

  const steps = [
    { icon: Link2, title: t('referrals.step1Title'), desc: t('referrals.step1Desc') },
    { icon: UserPlus, title: t('referrals.step2Title'), desc: t('referrals.step2Desc', { referee }) },
    { icon: Trophy, title: t('referrals.step3Title'), desc: t('referrals.step3Desc', { referrer: referrer }) },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="referrals-page">
      <div className="label-eyebrow text-[#2EE59D] mb-2 flex items-center gap-2"><Gift className="w-4 h-4" /> {t('referrals.eyebrow')}</div>
      <h1 className="font-display text-4xl sm:text-5xl">{t('referrals.title')}</h1>
      <p className="text-muted-foreground mt-2 max-w-xl">{t('referrals.subtitle', { referrer: referrer, referee })}</p>

      {/* Share link */}
      <div className="kite-card p-6 mt-6 border-[#2EE59D]/25">
        <div className="label-eyebrow text-muted-foreground mb-3">{t('referrals.yourLink')}</div>
        {loading ? (
          <Skeleton className="h-11 bg-white/10 rounded-xl" />
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <Input value={link} readOnly className="bg-[#0A0A0A] border-white/15 font-data text-sm flex-1" data-testid="referral-link-input" onFocus={(e) => e.target.select()} />
            <Button onClick={copy} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl shrink-0" data-testid="referral-copy-btn">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? t('referrals.copied') : t('referrals.copy')}
            </Button>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-muted-foreground mr-1">{t('referrals.shareVia')}</span>
          <a href={waHref} target="_blank" rel="noreferrer" data-testid="referral-share-whatsapp">
            <Button variant="outline" size="sm" className="bg-transparent border-white/15 hover:bg-white/10 rounded-lg">
              <MessageCircle className="w-4 h-4 mr-2 text-[#2EE59D]" /> WhatsApp
            </Button>
          </a>
          <a href={mailHref} data-testid="referral-share-email">
            <Button variant="outline" size="sm" className="bg-transparent border-white/15 hover:bg-white/10 rounded-lg">
              <Mail className="w-4 h-4 mr-2 text-[#48CAE4]" /> {t('referrals.email')}
            </Button>
          </a>
          {data?.code && (
            <Badge variant="outline" className="border-white/15 text-muted-foreground font-data ml-auto">
              {t('referrals.code')}: <span className="text-white ml-1" data-testid="referral-code">{data.code}</span>
            </Badge>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 bg-white/10 rounded-2xl" />)
        ) : (
          <>
            <StatCard icon={Users} label={t('referrals.invited')} value={data?.stats?.invited ?? 0} accent="#48CAE4" testid="ref-stat-invited" />
            <StatCard icon={Check} label={t('referrals.qualified')} value={data?.stats?.completed ?? 0} accent="#2EE59D" testid="ref-stat-qualified" />
            <StatCard icon={Coins} label={t('referrals.creditsEarned')} value={data?.stats?.credits_earned ?? 0} accent="#FFB020" testid="ref-stat-earned" />
          </>
        )}
      </div>

      {/* How it works */}
      <h2 className="font-display text-2xl mt-10 mb-4">{t('referrals.howTitle')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="kite-card p-5" data-testid={`referral-step-${i + 1}`}>
              <div className="w-10 h-10 rounded-xl bg-[#00B4D8]/10 border border-[#00B4D8]/30 flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-[#48CAE4]" />
              </div>
              <div className="font-medium">{s.title}</div>
              <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Invite list */}
      <h2 className="font-display text-2xl mt-10 mb-4">{t('referrals.yourInvites')}</h2>
      <div className="kite-card p-2">
        {loading ? (
          <div className="p-6"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00B4D8]" /></div>
        ) : !data?.referrals?.length ? (
          <div className="flex flex-col items-center text-center py-12 px-4" data-testid="referrals-empty">
            <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center mb-3">
              <Users className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm max-w-sm">{t('referrals.emptyDesc')}</p>
          </div>
        ) : (
          <div className="divide-y divide-white/8" data-testid="referrals-list">
            {data.referrals.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-4 py-3" data-testid="referral-row">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#12161B] border border-white/10 flex items-center justify-center text-sm font-semibold text-[#48CAE4] shrink-0">
                    {(r.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate text-sm">{r.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{r.email_masked}</div>
                  </div>
                </div>
                {r.status === 'completed' ? (
                  <Badge variant="outline" className="bg-[#2EE59D]/15 text-[#2EE59D] border-[#2EE59D]/30 shrink-0">
                    <Check className="w-3 h-3 mr-1" /> {t('referrals.statusQualified')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-[#FFB020]/15 text-[#FFB020] border-[#FFB020]/30 shrink-0">
                    {t('referrals.statusPending')}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
